<template>
  <v-card flat>
    <v-card-title class="pa-0">
      <h4>Confirm Review Actions</h4>
    </v-card-title>
    <v-card-text class="px-0 pb-3 pt-1">
      <p class="mb-0">
        Please review the changes you've made below, then confirm to finish the
        review. Please note that no more changes can be made after review
        submission. If a change needs to be made please ask a campaign admin to
        reset the asset
      </p>
      <section v-if="reviewsToConfirm.length === 0" key="no-reviews">
        <p class="font-weight-medium d-inline-block mb-0 pt-2">
          There is nothing to review.
        </p>
      </section>
      <section v-else id="reviewList" key="has-reviews">
        <article v-for="(review, idx) in reviewsToConfirm" :key="idx">
          <p class="font-weight-medium d-inline-block mb-0 pt-2">
            {{ review.name }}
          </p>
          <div>
            <div
              id="AssetReviewConfirmTable"
              class="asset-review-step-confirm-table-container"
            >
              <sphere-table
                ref="AssetReviewConfirmTable"
                :key="tableKey"
                :data-items="
                  review.name === 'Dependencies to Onboard'
                    ? review.items
                    : addChangesToItems(review.items)
                "
                :columns="AssetColumns[review.name]"
                name="Asset Review Step confirm"
              >
              </sphere-table>
            </div>
          </div>
          <v-divider />
        </article>
      </section>
    </v-card-text>
  </v-card>
</template>

<script>
/**
 * In this review step the asset owner can see the changes they've made during
 * the review before submitting.
 */

import { getFieldType } from '../../../static/QuestionType.js';
import reviewStepBase from './reviewStepBase.mixin.js';
import SphereTable from '../.././Table/SphereTable.vue';

export default {
  name: 'AssetReviewStepConfirm',
  components: { SphereTable },
  mixins: [reviewStepBase],
  props: {
    finalDependencies: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      tableKey: 0,
      rerenderAttributes: 1,
    };
  },
  computed: {
    hasAccessToReview() {
      return this.accessList.length > 0;
    },
    hasfunctionsToReview() {
      return this.functionList.length > 0;
    },
    hasAttributesToReview() {
      return this.attributes.length + this.customQuestions.length > 0;
    },
    reviewsToConfirm() {
      let reviews = [];
      if (this.hasAccessToReview && !this.readOnlyAccessReview) {
        let reqColumns = this.accessList.map((q) => {
          return {
            name: q.name,
            Email: q.Email,
            value: q.value,
            newValue: q.newValue,
            Changed: q.Changed,
            accessChangeReason: q.reason ? q.reason : '',
          };
        });
        reviews.push({ name: 'Access Review', items: [...reqColumns] });
      }
      if (this.hasAttributesToReview && this.rerenderAttributes > 0) {
        let questions = this.finalCustomQuestions.map((q) => {
          return {
            name: q.Question,
            value: q.value,
            newValue: q.newValue == null ? '' : q.newValue,
            type: q.Type,
          };
        });
        questions = questions.filter((q) => getFieldType(q.type) !== 'Label');
        reviews.push({
          name: 'Attributes',
          items: [...this.attributes, ...questions],
        });
      }
      if (this.functionList.length > 0) {
        reviews.push({
          name: 'Function Review',
          items: this.functionList.map((f) => {
            return { name: f.FunctionName, ...f };
          }),
        });
      }
      const dependencies = Object.values(this.finalDependencies);
      if (dependencies.length > 0) {
        reviews.push({
          name: 'Dependencies to Onboard',
          items: dependencies.map((d) => {
            return {
              Host: d.HostName,
              Instance: d.InstanceName,
              Onboard: d.ShouldBeOnboarded,
              CanRestartService: d.CanRestartService
                ? d.CanRestartService
                : null,
              Folder: d.TaskFolder,
              DisableAutomaticManagement: d.DisableAutomaticManagement,
              DisableAutomaticManagementReason:
                d.DisableAutomaticManagementReason,
              Changed:
                d.ServiceTypeId === 2
                  ? this.dependenciesChanges(d, undefined)
                  : this.dependenciesChanges(d, 'No'),
            };
          }),
        });
      }
      this.updateTableKey();
      return reviews;
    },
  },
  watch: {
    customQuestions: {
      deep: true,
      async handler() {
        this.forceRerender = false;
        await this.$nextTick();
        this.forceRerender = true;
      },
    },
    attributes: {
      deep: true,
      async handler(newValue) {
        newValue.forEach((n) => {
          if (n.value != n.newValue) this.rerenderAttributes += 1;
        });
      },
    },
  },
  async created() {
    this.AssetColumns = {
      Attributes: [
        {
          field: 'name',
          title: 'Name',
        },
        {
          field: 'value',
          title: 'Original',
        },
        {
          field: 'newValue',
          title: 'Final',
        },
        {
          field: 'Changed',
        },
        {
          field: 'selectable',
          invisible: true,
        },
      ],
      'Dependencies to Onboard': [
        {
          field: 'Host',
        },
        {
          field: 'Instance',
        },
        {
          field: 'Onboard',
        },
        {
          field: 'CanRestartService',
        },
        {
          field: 'Folder',
        },
        {
          field: 'DisableAutomaticManagement',
        },
        {
          field: 'DisableAutomaticManagementReason',
        },
        {
          field: 'Changed',
        },
      ],
      'Function Review': [
        {
          field: 'selected',
          invisible: true,
        },
        {
          field: 'OwnerId',
          invisible: true,
        },
        {
          field: 'FunctionName',
          invisible: true,
        },
        {
          field: 'FunctionDescription',
        },
      ],
    };
  },
  methods: {
    updateTableKey() {
      if (this.step && this.step.title === 'Review Confirmation')
        this.tableKey++;
    },
    addChangesToItems(items) {
      return items.map((i) => {
        i.Changed = i.value !== i.newValue;
        return i;
      });
    },
    dependenciesChanges(d, s) {
      let changedFlag =
        d.ShouldBeOnboarded === 'No'
          ? true
          : d.CanRestartService != s
          ? true
          : d.DisableAutomaticManagement == 'Yes'
          ? true
          : d.TaskFolder != null && d.TaskFolder != ''
          ? true
          : false;
      return changedFlag;
    },
  },
};
</script>

<style lang="scss">
.asset-review-step-confirm-table-container {
  .sphere-table-container {
    position: relative;
  }
  .k-grid {
    max-height: calc(100vh - 409px);
    min-height: 140px;
  }
}
</style>
